import { Dialog as RadixDialog } from "radix-ui";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { Icon } from "./icon";

export interface DialogProps {
	open?: boolean;
	defaultOpen?: boolean;
	onOpenChange?: (open: boolean) => void;
	trigger?: ReactNode;
	title: ReactNode;
	/** Подзаголовок под именем модалки: «Work and Travel USA — IEC services part». */
	description?: ReactNode;
	/** Кнопки внизу. Порядок макета: сначала отмена, потом подтверждение. */
	footer?: ReactNode;
	width?: number;
	children?: ReactNode;
	className?: string;
	/** Подпись крестика. Обязательна: текстовых умолчаний у кита нет (аудит В1). */
	closeLabel: string;
}

/**
 * Модальное окно: r12, --shadow-float, ширина 480 по умолчанию.
 * Затемнение — чёрный 50% с блюром 8px; вход 500ms по --ease-elegant.
 *
 * Крестик стоит в шапке справа, как в макете «Pay with points», и это
 * единственный способ закрыть окно с клавиатуры помимо Esc.
 */
export function Dialog({
	open,
	defaultOpen,
	onOpenChange,
	trigger,
	title,
	description,
	footer,
	width = 480,
	children,
	className,
	closeLabel,
}: DialogProps) {
	return (
		<RadixDialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
			{trigger ? <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger> : null}
			<RadixDialog.Portal>
				<RadixDialog.Overlay
					className={cn(
						"fixed inset-0 z-120 bg-scrim backdrop-blur-[8px]",
						"data-[state=open]:[animation:iec-scrim-in_var(--dur-300)_var(--ease-smooth)]",
					)}
				/>
				<RadixDialog.Content
					style={{ width }}
					className={cn(
						"-translate-x-1/2 -translate-y-1/2 fixed top-1/2 left-1/2 z-120",
						"flex max-h-[calc(100dvh-40px)] max-w-[calc(100vw-20px)] flex-col gap-5 overflow-hidden",
						"rounded-card bg-card p-5 shadow-float outline-none",
						"data-[state=open]:[animation:iec-reveal_var(--dur-500)_var(--ease-elegant)]",
						className,
					)}
				>
					<header className="flex items-start justify-between gap-4">
						<div className="flex min-w-0 flex-col gap-1.5">
							<RadixDialog.Title className="t-section">{title}</RadixDialog.Title>
							{description != null ? (
								<RadixDialog.Description className="t-body text-fg-muted">
									{description}
								</RadixDialog.Description>
							) : null}
						</div>
						<RadixDialog.Close
							aria-label={closeLabel}
							className={cn(
								"iec-press-glyph tap-target inline-flex shrink-0 text-fg transition-colors duration-150 ease-smooth",
								"hover:text-fg-muted",
								"focus-ring",
							)}
						>
							<Icon name="X" size={24} />
						</RadixDialog.Close>
					</header>
					{/*
					 * `px-1 -mx-1` — не отступ, а место под кольцо фокуса.
					 * У `.iec-scroll` стоит `overflow-y: auto`, а по спецификации
					 * это превращает `visible` и по второй оси в обрезку: halo
					 * поля (`box-shadow 0 0 0 3px`) срезался ровно по краям
					 * окна. Внутренний паддинг даёт ему место, отрицательный
					 * margin возвращает контент на прежнее место — визуально
					 * ничего не сдвигается.
					 */}
					<div className="iec-scroll -mx-1 flex flex-col gap-5 px-1">{children}</div>
					{footer != null ? <footer className="flex gap-1">{footer}</footer> : null}
				</RadixDialog.Content>
			</RadixDialog.Portal>
		</RadixDialog.Root>
	);
}

export const DialogClose = RadixDialog.Close;
